import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress } from '@mui/material';
import apiClient from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import ModalResult from '../../components/ModalResult';
import useMonthOptions from '../../hooks/useMonthOptions';
import CourseMembershipRequiredBanner from './components/CourseMembershipRequiredBanner';
import CourseYearFilters from './components/CourseYearFilters';
import CourseGrid from './components/CourseGrid';
import CourseEnrollmentDialog from './components/CourseEnrollmentDialog';

const Courses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' | 'error'
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrollForm, setEnrollForm] = useState({
    paymentMethod: 'monthly',
    startDate: '', // Mese di inizio iscrizione
  });
  const [selectedYearFilter, setSelectedYearFilter] = useState('all');

  // Hook per generare le opzioni dei mesi
  const monthOptions = useMonthOptions(selectedCourse);

  const fetchData = async () => {
    setLoading(true);
    try {
      const coursesRes = await apiClient.get('/api/courses?status=published');
      setCourses(coursesRes.data);
    } catch (error) {
      console.log(error);
      setError('Errore nel caricamento dei corsi');
      setModalType('error');
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Imposta il filtro di default basato sul livello dell'utente
  useEffect(() => {
    if (user.level >= 4) {
      setSelectedYearFilter('post-triennio');
    } else if (user.level >= 1) {
      setSelectedYearFilter(user.level.toString());
    } else {
      setSelectedYearFilter('0');
    }
  }, [user]);

  const handleEnroll = (course) => {
    setSelectedCourse(course);
    setEnrollForm({
      paymentMethod: 'monthly',
      startDate: '', // Reset del mese di inizio
    });
    setEnrollDialogOpen(true);
  };

  const handleEnrollSubmit = async () => {
    // Naviga al nuovo workflow passando i dati del corso selezionato
    if (!selectedCourse || !enrollForm.startDate) return;
    const courseData = {
      _id: selectedCourse._id,
      name: selectedCourse.name,
      description: selectedCourse.description,
      courseStartDate: selectedCourse.startDate, // Data di inizio del corso
      courseEndDate: selectedCourse.endDate, // Data di fine del corso
      enrollmentStartDate: enrollForm.startDate, // Data di inizio iscrizione selezionata dall'utente
      amount: calculatePaymentAmount(enrollForm.paymentMethod),
      paymentMethod: enrollForm.paymentMethod,
      venue: selectedCourse.venue,
      pricing: selectedCourse.pricing,
    };
    setEnrollDialogOpen(false);
    navigate('/course-enrollment-checkout', { state: { courseData } });
  };

  const handleEnrollFormChange = (updater) => {
    setEnrollForm(updater);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSuccess(null);
    setError(null);
  };

  const isEnrolled = (courseId) => {
    if (!Array.isArray(user?.enrollments)) return null;
    return user.enrollments.find((enrollment) => enrollment.courseId === courseId) || null;
  };

  const calculatePaymentAmount = (paymentMethod) => {
    if (!selectedCourse?.pricing) {
      return 'N/A';
    }

    switch (paymentMethod) {
      case 'monthly':
        return selectedCourse.pricing.monthly;
      case 'annual3':
        return selectedCourse.pricing.annual3;
      case 'annual1':
        return selectedCourse.pricing.annual1;
      default:
        return selectedCourse.pricing.monthly;
    }
  };

  // Funzione per controllare se l'utente ha un tesseramento valido (attivo o in attesa)
  const hasValidMembership = () => {
    if (!user || !Array.isArray(user.memberships)) return false;
    const now = new Date();
    return user.memberships.some((m) => new Date(m.endDate) > now);
  };

  // Funzione per controllare se l'utente può iscriversi al corso basato sul livello
  const canEnrollInCourse = (course) => {
    // Controlla se il corso richiede tessera associativa
    if (course.isMembershipRequired && !hasValidMembership()) {
      return false; // Utente senza tessera valida non può iscriversi a corsi che la richiedono
    }

    // if (!course.minLevel) return true; // Corsi senza minLevel sono accessibili a tutti
    // if (!user?.level) return false; // Utente senza livello non può iscriversi a corsi con requisiti
    return user.level >= course.minLevel;
  };

  // Ritorna i corsi con costo diverso da 0 per ogni chiave di pricing
  // Ritorna i corsi filtrati per il livello dall'utente nel tab selectedYearFilter
  const getFilteredCourses = () => {
    let filteredCourses = courses;

    filteredCourses = filteredCourses.filter((course) => {
      const { monthly, annual3, annual1 } = course.pricing;
      return !(monthly === 0 && annual3 === 0 && annual1 === 0);
    });

    if (selectedYearFilter === 'post-triennio') {
      return filteredCourses.filter((course) => course.minLevel >= 4);
    }

    // Filtra per anno specifico
    const yearLevel = parseInt(selectedYearFilter);
    return filteredCourses.filter((course) => course.minLevel === yearLevel);
  };

  const handleYearFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setSelectedYearFilter(newFilter);
    }
  };

  const validMembership = hasValidMembership();
  const filteredCourses = getFilteredCourses();
  const someCourseRequiresMembership = filteredCourses.some((course) => course.isMembershipRequired);

  if (loading) {
    return (
      <Container maxWidth='lg'>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg'>
      <Box sx={{ py: 4 }}>
        {!validMembership && someCourseRequiresMembership && <CourseMembershipRequiredBanner />}

        <CourseYearFilters selectedYearFilter={selectedYearFilter} onFilterChange={handleYearFilterChange} />

        <ModalResult open={modalOpen} onClose={handleCloseModal} message={success || error} severity={modalType} />

        <CourseGrid filteredCourses={filteredCourses} selectedYearFilter={selectedYearFilter} isEnrolled={isEnrolled} validMembership={validMembership} canEnrollInCourse={canEnrollInCourse} onEnroll={handleEnroll} />

        <CourseEnrollmentDialog open={enrollDialogOpen} onClose={() => setEnrollDialogOpen(false)} selectedCourse={selectedCourse} enrollForm={enrollForm} onFormChange={handleEnrollFormChange} monthOptions={monthOptions} onSubmit={handleEnrollSubmit} calculatePaymentAmount={calculatePaymentAmount} />
      </Box>
    </Container>
  );
};

export default Courses;
